window.IndustriesPage = function IndustriesPage({ 
  selectedIndustrySlug = 'railway',
  setActiveTab, 
  openQuoteModal,
  setSelectedProductSlug,
  setSelectedProductCategory,
  setSelectedCategoryData
}) {
  const [industries, setIndustries] = React.useState([]);
  const [allProducts, setAllProducts] = React.useState([]);
  const [activeSlug, setActiveSlug] = React.useState(selectedIndustrySlug || 'railway');
  const [searchQuery, setSearchQuery] = React.useState('');

  React.useEffect(() => {
    fetchIndustries();
    fetchProducts();
    const handleSync = () => {
      fetchIndustries();
      fetchProducts();
    };
    window.addEventListener('novatek_content_updated', handleSync);
    window.addEventListener('storage', handleSync);
    return () => {
      window.removeEventListener('novatek_content_updated', handleSync);
      window.removeEventListener('storage', handleSync);
    };
  }, []);

  const fetchIndustries = async () => {
    try {
      const res = await window.apiClient.get('content.php');
      if (res.success && res.data && res.data.industries_data && Array.isArray(res.data.industries_data)) {
        const visible = res.data.industries_data.filter(i => i.visible !== false);
        if (visible.length > 0) setIndustries(visible);
      }
    } catch (e) {
      console.warn('IndustriesPage CMS sync warning:', e);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await window.apiClient.get('products.php');
      if (res.success && res.data && res.data.length > 0) {
        setAllProducts(res.data);
      }
    } catch (e) {
      console.warn('Products fetch fallback:', e);
    }
  };

  React.useEffect(() => {
    if (selectedIndustrySlug) {
      setActiveSlug(selectedIndustrySlug);
    }
  }, [selectedIndustrySlug]);

  const currentIndustry = industries.find(ind => ind.slug === activeSlug || ind.id === activeSlug) || industries[0] || {};

  React.useEffect(() => {
    if (currentIndustry && currentIndustry.title) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (window.SEOHelper) {
        window.SEOHelper.updatePageMeta(
          currentIndustry.seoTitle || `${currentIndustry.title} | Novatek`,
          currentIndustry.shortDesc || currentIndustry.heroDesc
        );
      }
    }
  }, [activeSlug, currentIndustry]);

  const industryProducts = allProducts.filter(p => 
    (currentIndustry.featuredProducts && currentIndustry.featuredProducts.some(id => String(id) === String(p.id))) ||
    (currentIndustry.id && p.category && p.category.toLowerCase().includes(currentIndustry.id.toLowerCase()))
  );

  const filteredIndustries = industries.filter(ind => 
    !searchQuery || ind.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
    (ind.heroDesc && ind.heroDesc.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="bg-slate-50 min-h-screen py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8 sm:space-y-10">
        
        {/* Page Title */}
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <span className="font-sans text-xs sm:text-sm font-bold text-secondary tracking-widest uppercase block">
            Domain Specific Engineering
          </span>
          <h1 className="font-sans text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Industry Applications & Test Rigs
          </h1>
          <div className="h-1 w-20 bg-secondary mx-auto rounded-full"></div>
          <p className="font-sans text-slate-600 text-sm sm:text-base">
            Custom engineered test benches built to meet national and international standards across critical sectors.
          </p>
        </div>

        {/* Clean Modern Industry Category Bar & Search */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-sm space-y-3">
          <div className="flex flex-col lg:flex-row gap-4 justify-between items-stretch lg:items-center">
            
            {/* Industry Segmented Pills */}
            <div className="flex flex-wrap items-center gap-2 flex-1">
              {filteredIndustries.map((ind) => {
                const isSelected = activeSlug === ind.slug || activeSlug === ind.id;
                return (
                  <button
                    key={ind.id || ind.slug}
                    onClick={() => setActiveSlug(ind.slug || ind.id)}
                    className={`px-4 py-2.5 rounded-xl font-sans text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
                      isSelected
                        ? 'bg-slate-900 text-white shadow-md'
                        : 'bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200/80'
                    }`}
                  >
                    <span>{ind.title.split('&')[0]}</span>
                    {isSelected && (
                      <span className="h-2 w-2 rounded-full bg-secondary"></span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Quick Search */}
            <div className="relative w-full lg:w-72 shrink-0">
              <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-base">
                search
              </span>
              <input
                type="text"
                placeholder="Filter industries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-sans outline-none focus:border-secondary focus:bg-white transition-all shadow-2xs"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-xs p-1"
                >
                  ✕
                </button>
              )}
            </div>

          </div>
        </div>

        {/* Industry Hero */}
        <div className="bg-slate-900 text-white rounded-3xl border border-slate-800 p-8 sm:p-12 space-y-6 relative overflow-hidden shadow-2xl">
          <div className="max-w-3xl space-y-4 relative z-10">
            <span className="font-sans text-xs sm:text-sm font-bold text-secondary tracking-widest uppercase bg-orange-950/70 border border-orange-500/40 px-3.5 py-1.5 rounded-md inline-block">
              {currentIndustry.eyebrow || "MISSION CRITICAL"}
            </span>
            <h2 className="font-sans text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight">
              {currentIndustry.h1 || currentIndustry.title}
            </h2>
            <p className="font-sans text-slate-300 text-base sm:text-lg leading-relaxed">
              {currentIndustry.heroDesc || currentIndustry.shortDesc}
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <button
                onClick={openQuoteModal}
                className="bg-secondary hover:bg-orange-600 text-white font-sans font-bold text-sm px-8 py-3.5 rounded-xl shadow-lg transition-all active:scale-95 flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-lg">engineering</span>
                <span>Request Domain Consultation</span>
              </button>
            </div>
          </div>

          <div className="absolute right-0 bottom-0 opacity-10 pointer-events-none">
            <span className="material-symbols-outlined text-[300px]">factory</span>
          </div>
        </div>

        {/* Image Gallery */}
        {currentIndustry.gallery && currentIndustry.gallery.length > 0 && (
          <div className="space-y-6 pt-6">
            <div className="space-y-1">
              <span className="font-sans text-xs font-bold text-secondary tracking-widest uppercase block">
                Visual Overview
              </span>
              <h3 className="font-sans text-2xl font-bold text-slate-900">
                Industry Applications Gallery
              </h3>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentIndustry.gallery.map((imgUrl, idx) => (
                <div key={idx} className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm aspect-video bg-slate-100 group">
                  <img 
                    src={window.resolveMediaUrl(imgUrl, 'industry')} 
                    alt={`${currentIndustry.title} Gallery Image ${idx + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => window.handleImageError(e, 'industry')}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sub-sectors / Testing Applications */}
        {currentIndustry.subSectors && currentIndustry.subSectors.length > 0 && (
          <div className="space-y-6">
            <div className="space-y-1">
              <span className="font-sans text-xs font-bold text-secondary tracking-widest uppercase block">
                Sub-Domain Specializations
              </span>
              <h3 className="font-sans text-2xl font-bold text-slate-900">
                {currentIndustry.subSectorsTitle || "Application Test Systems & Validation Modules"}
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentIndustry.subSectors.map((sub, idx) => (
                <div key={idx} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-lg transition-all group flex flex-col justify-between">
                  <div>
                    {sub.image ? (
                      <div className="h-48 bg-slate-900 relative overflow-hidden">
                        <img 
                          src={window.resolveMediaUrl(sub.image, 'industry')} 
                          alt={sub.title} 
                          onError={(e) => window.handleImageError(e, 'industry')}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                        />
                        <div className="absolute top-3 left-3 bg-slate-900/80 text-white h-8 w-8 rounded-lg flex items-center justify-center font-sans font-bold text-sm border border-slate-700/50 backdrop-blur-sm shadow-md">
                          {idx + 1}
                        </div>
                      </div>
                    ) : (
                      <div className="h-48 bg-slate-100 flex items-center justify-center relative border-b border-slate-200">
                         <span className="material-symbols-outlined text-4xl text-slate-300">image</span>
                         <div className="absolute top-3 left-3 bg-white text-secondary h-8 w-8 rounded-lg flex items-center justify-center font-sans font-bold text-sm border border-slate-200 shadow-sm">
                          {idx + 1}
                        </div>
                      </div>
                    )}
                    
                    <div className="p-5 space-y-3">
                      <h4 className="font-sans text-lg font-bold text-slate-900 group-hover:text-secondary transition-colors">
                        {sub.title}
                      </h4>
                      <p className="font-sans text-sm text-slate-600 leading-relaxed line-clamp-3">
                        {sub.desc}
                      </p>
                    </div>
                  </div>

                  {(sub.linkToProduct || (sub.linkedProducts && sub.linkedProducts.length > 0)) && (
                    <div 
                      onClick={() => {
                        const productIds = sub.linkedProducts || (sub.linkToProduct ? [sub.linkToProduct] : []);
                        if (setSelectedCategoryData) {
                          setSelectedCategoryData({
                            title: sub.title,
                            desc: sub.desc,
                            productIds: productIds,
                            industryTitle: currentIndustry.title
                          });
                        }
                        setActiveTab('category');
                      }}
                      className="px-5 py-3.5 bg-slate-50 border-t border-slate-100 flex justify-between items-center text-sm font-sans font-bold text-secondary cursor-pointer hover:bg-slate-100 transition-colors"
                    >
                      <span>Explore Dedicated Rigs</span>
                      <span className="material-symbols-outlined text-base group-hover:translate-x-1 transition-transform">arrow_forward</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Challenges Solved */}
        {currentIndustry.challengesSolved && currentIndustry.challengesSolved.length > 0 && (
          <div className="bg-white p-8 rounded-3xl border border-slate-200 space-y-6 shadow-sm">
            <div className="space-y-1">
              <span className="font-sans text-xs font-bold text-secondary tracking-widest uppercase block">
                Engineering Value
              </span>
              <h3 className="font-sans text-2xl font-bold text-slate-900">
                Critical Engineering Challenges Solved
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {currentIndustry.challengesSolved.map((ch, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="material-symbols-outlined text-green-600 shrink-0 text-xl mt-0.5">check_circle</span>
                  <p className="font-sans text-xs font-semibold text-slate-700 leading-relaxed">{ch}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Related Products & Machines */}
        {industryProducts.length > 0 && (
          <div className="space-y-6 pt-6 border-t border-slate-200">
            <div className="flex justify-between items-end">
              <div>
                <span className="font-sans text-xs font-bold text-secondary tracking-widest uppercase block">
                  Equipment Portfolio
                </span>
                <h3 className="font-sans text-2xl font-bold text-slate-900">
                  Machines Deployed in {currentIndustry.title.split('&')[0]}
                </h3>
              </div>
              <button 
                onClick={() => {
                  if (setSelectedProductCategory) {
                    const targetCategory = currentIndustry.viewAllCategory || currentIndustry.title.split('&')[0].trim();
                    setSelectedProductCategory(targetCategory);
                  }
                  setActiveTab('products');
                }}
                className="text-xs font-sans font-bold text-secondary hover:text-orange-700 flex items-center gap-1"
              >
                <span>View All Systems</span>
                <span className="material-symbols-outlined text-sm">arrow_forward</span>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {industryProducts.slice(0, 4).map((prod) => (
                <div 
                  key={prod.id} 
                  onClick={() => {
                    if (setSelectedProductSlug) setSelectedProductSlug(prod.slug || prod.id);
                    setActiveTab('product-detail');
                  }}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group flex flex-col justify-between"
                >
                  <div>
                    <div className="h-44 bg-slate-900 relative overflow-hidden">
                      <img 
                        src={window.resolveMediaUrl(prod.primary_image || prod.image, 'machine')} 
                        alt={prod.name} 
                        onError={(e) => window.handleImageError(e, 'machine')}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform" 
                      />
                      <div className="absolute top-2.5 left-2.5 bg-slate-900/80 text-secondary text-[11px] font-sans font-bold px-2 py-0.5 rounded">
                        {prod.category}
                      </div>
                    </div>

                    <div className="p-4 space-y-2">
                      <h4 className="font-sans text-sm font-bold text-slate-900 group-hover:text-secondary transition-colors">
                        {prod.name}
                      </h4>
                      <p className="font-sans text-xs text-slate-600 line-clamp-2">
                        {prod.short_description || prod.description}
                      </p>
                    </div>
                  </div>

                  <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex justify-between items-center text-xs font-sans font-bold text-secondary">
                    <span>Explore Machine</span>
                    <span className="material-symbols-outlined text-sm group-hover:translate-x-1 transition-transform">arrow_forward</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
